/**
 * Glass-styled hero card for the learner experience.
 * Slot-based layout: leading visual, text stack, trailing actions.
 *
 * Use for course resume banners, streak milestones, or any prominent CTA card.
 */
import { type ReactNode, type CSSProperties } from 'react';
import { Surface } from './Surface';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HeroCardProps {
  /** Left visual element — progress ring, gradient number, icon, avatar, etc. */
  leading?: ReactNode;
  /** Small uppercase label above the title */
  eyebrow?: string;
  /** Colour for the eyebrow text (defaults to --color-text-muted) */
  eyebrowColor?: string;
  /** Primary heading */
  title: string;
  /** Supporting text below the title */
  subtitle?: ReactNode;
  /** Right-side actions — buttons, links, etc. */
  actions?: ReactNode;
  /** Additional inline styles */
  style?: CSSProperties;
  /** Additional className */
  className?: string;
}

// ─── Pre-built leading elements ───────────────────────────────────────────────

/** Circular progress ring for use as HeroCard leading. */
export function ProgressRing({
  value,
  size = 72,
  strokeWidth = 4,
  color = 'var(--color-accent)',
}: {
  value: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}) {
  const r = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--color-surface-track, var(--color-border))"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          fontSize: size * 0.22,
          fontWeight: 700,
          color,
          letterSpacing: '-0.025em',
          fontFamily: 'var(--font-display)',
        }}
      >
        {value}%
      </div>
    </div>
  );
}

/** Large gradient number for use as HeroCard leading (e.g. streak count). */
export function GradientNumber({
  value,
  gradient = 'var(--color-brand-gradient)',
}: {
  value: number | string;
  gradient?: string;
}) {
  return (
    <div
      style={{
        fontSize: 52,
        fontWeight: 800,
        letterSpacing: '-0.03em',
        lineHeight: 1,
        background: gradient,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        flexShrink: 0,
        fontFamily: 'var(--font-display)',
      }}
    >
      {value}
    </div>
  );
}

// ─── HeroCard ─────────────────────────────────────────────────────────────────

export function HeroCard({
  leading,
  eyebrow,
  eyebrowColor,
  title,
  subtitle,
  actions,
  style,
  className,
}: HeroCardProps) {
  return (
    <Surface
      className={className}
      style={{
        display: 'flex',
        gap: 28,
        alignItems: 'center',
        fontFamily: 'var(--font-sans)',
        fontWeight: 400,
        ...style,
      }}
      padding={28}
    >
      {/* Leading visual */}
      {leading}

      {/* Text stack */}
      <div className="min-w-0 flex-1">
        {eyebrow && (
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: eyebrowColor ?? 'var(--color-text-muted)',
              marginBottom: 6,
            }}
          >
            {eyebrow}
          </p>
        )}
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            fontWeight: 700,
            color: 'var(--color-text)',
            letterSpacing: '-0.025em',
            lineHeight: 1.2,
            marginBottom: subtitle ? 5 : 0,
          }}
        >
          {title}
        </h2>
        {subtitle &&
          (typeof subtitle === 'string' ? (
            <p
              style={{
                fontSize: 14,
                fontWeight: 400,
                color: 'var(--color-text-muted)',
                lineHeight: 1.55,
              }}
            >
              {subtitle}
            </p>
          ) : (
            <div
              style={{
                fontSize: 14,
                fontWeight: 400,
                color: 'var(--color-text-muted)',
                lineHeight: 1.55,
              }}
            >
              {subtitle}
            </div>
          ))}
      </div>

      {/* Actions */}
      {actions && <div className="flex shrink-0 items-center gap-3">{actions}</div>}
    </Surface>
  );
}
